"use client";

import { clearAuthCookies } from "@/actions/auth/clearAuthCookies";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { logout } from "@/store/slices/cred/credSlice";
import {
  ChefHat,
  LogIn,
  LogOut,
  MapPin,
  ShoppingBag,
  Sparkles,
  User,
  Utensils,
} from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";

interface UserMenuProps {
  /** Position of the menu */
  position?:
    | "top-right"
    | "top-left"
    | "bottom-right"
    | "bottom-left"
    | "inline";
  /** Custom class */
  className?: string;
}

export function UserMenu({
  position = "top-right",
  className = "",
}: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const router = useRouter();
  const dispatch = useAppDispatch();

  // Get auth state from Redux
  const { isAuthenticated, customer } = useAppSelector((state) => state.cred);

  // Build redirect URL for login/register
  const redirectUrl = encodeURIComponent(pathname || "/");

  // Handle logout
  const handleLogout = async () => {
    setIsOpen(false);
    await clearAuthCookies();
    dispatch(logout());
    router.push("/");
    router.refresh();
  };

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  // Position classes
  const positionClasses = {
    "top-right": "fixed top-4 right-4 sm:top-6 sm:right-6",
    "top-left": "fixed top-4 left-4 sm:top-6 sm:left-6",
    "bottom-right": "fixed bottom-4 right-4 sm:bottom-6 sm:right-6",
    "bottom-left": "fixed bottom-4 left-4 sm:bottom-6 sm:left-6",
    inline: "relative",
  };

  // Dropdown position based on button position
  const dropdownPosition = {
    "top-right": "right-0 mt-3",
    "top-left": "left-0 mt-3",
    "bottom-right": "right-0 mb-3 bottom-full",
    "bottom-left": "left-0 mb-3 bottom-full",
    inline: "right-0 mt-3",
  };

  // Get user initials for avatar
  const getInitials = () => {
    if (customer?.first_name && customer?.last_name) {
      return `${customer.first_name[0]}${customer.last_name[0]}`.toUpperCase();
    }
    return "G";
  };

  return (
    <div
      ref={menuRef}
      className={`${positionClasses[position]} z-50 ${className}`}
    >
      {/* Premium Menu Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`
          group relative flex items-center justify-center
          p-2 sm:p-2.5
          rounded-full
          bg-black/20 backdrop-blur-xl
          border border-white/20
          shadow-[0_8px_32px_rgba(0,0,0,0.12)]
          hover:bg-black/30 hover:border-white/30
          hover:shadow-[0_12px_40px_rgba(0,0,0,0.2)]
          active:scale-[0.98]
          transition-all duration-300 ease-out cursor-pointer
        `}
        aria-label="User menu"
        aria-expanded={isOpen}
      >
        {/* Animated ring */}
        <span
          className={`
            absolute inset-0 rounded-full
            bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-red-500/20
            opacity-0 group-hover:opacity-100
            transition-opacity duration-300
            blur-sm -z-10
          `}
        />

        {/* Avatar/Icon */}
        {isAuthenticated ? (
          <span className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 text-white text-xs sm:text-sm font-semibold shadow-inner">
            {getInitials()}
          </span>
        ) : (
          <span className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-white/10 text-white/90">
            <User className="h-4 w-4 sm:h-5 sm:w-5" />
          </span>
        )}
      </button>

      {/* Dropdown Menu with animation */}
      <div
        className={`
          absolute ${dropdownPosition[position]} 
          w-72 sm:w-80
          transform origin-top-right
          transition-all duration-300 ease-out
          ${
            isOpen
              ? "opacity-100 scale-100 translate-y-0"
              : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
          }
        `}
      >
        <div
          className="
            overflow-hidden rounded-2xl
            bg-white/95 backdrop-blur-2xl
            border border-gray-200/50
            shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)]
          "
        >
          {isAuthenticated ? (
            <>
              {/* User Profile Header */}
              <div className="relative px-5 py-5 bg-gradient-to-br from-amber-50 to-orange-50 border-b border-amber-100/50">
                {/* Decorative pattern */}
                <div className="absolute top-0 right-0 w-24 h-24 opacity-10">
                  <Utensils className="w-full h-full text-amber-600" />
                </div>

                <div className="relative flex items-center gap-4">
                  <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white text-lg font-bold shadow-lg shadow-orange-500/25">
                    {getInitials()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-semibold text-gray-900 truncate">
                      {customer?.first_name} {customer?.last_name}
                    </p>
                    <p className="text-sm text-gray-500 truncate">
                      {customer?.email}
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      <Sparkles className="w-3 h-3 text-amber-500" />
                      <span className="text-xs text-amber-600 font-medium">
                        Valued Guest
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <nav className="py-2">
                <Link
                  href="/profile"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-3 px-5 py-3 text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors group"
                >
                  <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-gray-100 group-hover:bg-amber-100 transition-colors">
                    <User className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-sm font-medium">My Profile</p>
                    <p className="text-xs text-gray-400">Manage your account</p>
                  </div>
                </Link>

                <Link
                  href="/profile?tab=orders"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-3 px-5 py-3 text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors group"
                >
                  <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-gray-100 group-hover:bg-amber-100 transition-colors">
                    <ShoppingBag className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-sm font-medium">Order History</p>
                    <p className="text-xs text-gray-400">View past orders</p>
                  </div>
                </Link>

                <Link
                  href="/profile?tab=addresses"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-3 px-5 py-3 text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors group"
                >
                  <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-gray-100 group-hover:bg-amber-100 transition-colors">
                    <MapPin className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-sm font-medium">Saved Addresses</p>
                    <p className="text-xs text-gray-400">Delivery locations</p>
                  </div>
                </Link>
              </nav>

              {/* Logout */}
              <div className="border-t border-gray-100 p-2">
                <button
                  onClick={handleLogout}
                  className="flex items-center cursor-pointer gap-3 w-full px-4 py-2.5 rounded-xl text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="text-sm font-medium">Sign Out</span>
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Guest Welcome Header */}
              <div className="relative px-5 py-6 bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 border-b border-amber-100/50 overflow-hidden">
                {/* Decorative elements */}
                <div className="absolute -top-4 -right-4 w-24 h-24 opacity-10">
                  <ChefHat className="w-full h-full text-amber-600" />
                </div>
                <div className="absolute -bottom-2 -left-2 w-16 h-16 opacity-10">
                  <Utensils className="w-full h-full text-orange-600" />
                </div>

                <div className="relative text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-lg shadow-orange-500/25 mb-3">
                    <ChefHat className="w-8 h-8" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Welcome, Food Lover!
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Sign in for personalized experience
                  </p>
                </div>
              </div>

              {/* Auth Actions */}
              <div className="p-4 space-y-2">
                <Link
                  href={`/cred/login?redirect=${redirectUrl}`}
                  onClick={() => setIsOpen(false)}
                  className="
                    flex items-center justify-center gap-2 w-full
                    px-4 py-3 rounded-xl
                    bg-gradient-to-r from-amber-500 to-orange-600
                    text-white font-medium text-sm
                    shadow-lg shadow-orange-500/25
                    hover:shadow-xl hover:shadow-orange-500/30
                    hover:from-amber-600 hover:to-orange-700
                    active:scale-[0.98]
                    transition-all duration-200
                  "
                >
                  <LogIn className="h-4 w-4" />
                  Sign In
                </Link>

                <Link
                  href={`/cred/register?redirect=${redirectUrl}`}
                  onClick={() => setIsOpen(false)}
                  className="
                    flex items-center justify-center gap-2 w-full
                    px-4 py-3 rounded-xl
                    bg-gray-100 text-gray-700 font-medium text-sm
                    hover:bg-gray-200
                    active:scale-[0.98]
                    transition-all duration-200
                  "
                >
                  <User className="h-4 w-4" />
                  Create Account
                </Link>
              </div>

              {/* Benefits hint */}
              <div className="px-5 py-3 bg-gray-50 border-t border-gray-100">
                <p className="text-xs text-gray-400 text-center">
                  ✨ Earn rewards • Save favorites • Quick reorder
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserMenu;
